import {useEffect, useMemo, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import moment from "moment";
import {Button} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {generateRosterMatrixPDF} from "@/utils/generateRosterMatrixPDF";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import HomeIcon from "@mui/icons-material/Home";
import FlightIcon from "@mui/icons-material/Flight";
import VLFSelectNew from "@/components/Input/SelectNew";
import ChoiceChips from "@/components/ChoiceChips";
import MainActions from "@/components/MainActions";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFTextField from "@/components/Input/TextField";
import {RosterAPI} from "@/api/roster";
import {
	MATRIX_STATUSES,
	MATCH_MATRIX_SUMMARY_METRICS,
	matrixSummaryMetric,
	summarizeMatrixRow,
} from "@/utils/rosterActivityMatrix";
import {trainingTypeColors} from "@/utils/trainingTypes";
import TrainingTypes from "./TrainingTypes";
import TrainingTypesMatrix from "./TrainingTypesMatrix";
import "./index.scss";

const ALL = {value: "", label: "Tutte le competizioni"};
const initialDate = (season) => {
	const now = moment();
	return season?.startDate &&
		(now.isBefore(season.startDate) || now.isAfter(season.endDate))
		? moment(season.startDate).format("YYYY-MM-DD")
		: now.format("YYYY-MM-DD");
};
export default function RosterActivityMatrices({
	rosterId,
	season,
	kind = "training",
}) {
	const navigate = useNavigate();
	const [trainingView, setTrainingView] = useState("attendance");
	const [trainingScope, setTrainingScope] = useState("completed"),
		[trainingMetric, setTrainingMetric] = useState("minutes");
	const [period, setPeriod] = useState("month"),
		[date, setDate] = useState(() => initialDate(season));
	const [competition, setCompetition] = useState(ALL),
		[search, setSearch] = useState("");
	const [exporting, setExporting] = useState(false);
	const [data, setData] = useState(null),
		[loading, setLoading] = useState(true),
		[error, setError] = useState("");
	useEffect(() => {
		setDate(initialDate(season));
		setCompetition(ALL);
		setSearch("");
	}, [rosterId, season?.startDate, season?.endDate]);
	const fromDate = moment(date)
		.startOf(period === "week" ? "isoWeek" : "month")
		.format("YYYY-MM-DD");
	const toDate = moment(date)
		.endOf(period === "week" ? "isoWeek" : "month")
		.format("YYYY-MM-DD");
	useEffect(() => {
		let active = true;
		setLoading(true);
		setError("");
		setData(null);
		RosterAPI.activityMatrix(rosterId, {
			kind,
			fromDate,
			toDate,
			...(kind === "match" && competition.value
				? {competitionSeasonId: competition.value}
				: {}),
		})
			.then((result) => {
				if (active) setData(result);
			})
			.catch((e) => {
				if (active)
					setError(
						e.response?.data?.error || "Impossibile caricare la matrice.",
					);
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [rosterId, kind, fromDate, toDate, competition.value]);
	const rows = useMemo(
		() =>
			(data?.players || []).filter((p) =>
				p.name
					.toLocaleLowerCase("it")
					.includes(search.trim().toLocaleLowerCase("it")),
			),
		[data, search],
	);
	const typeColors = useMemo(
		() => trainingTypeColors(data?.trainingAnalysis),
		[data?.trainingAnalysis],
	);
	const options = [
		ALL,
		...(data?.competitions || []).map((c) => ({value: c.id, label: c.name})),
	];
	const columns = data?.columns || [];
	const statuses =
		kind === "training"
			? ["present", "absent", "injured", "scheduled", "unrecorded", "check"]
			: [
					"starter",
					"substitute",
					"bench",
					"lineup",
					"called",
					"not-called",
					"injured",
					"scheduled",
					"unrecorded",
					"check",
				];
	const open = (column) =>
		navigate(
			(kind === "training" ? "/trainings/" : "/matches/") + column.eventId,
			{
				state: {
					returnTo: "/manager/teams/" + rosterId,
					initialTab: kind === "match" ? "match_report_players" : undefined,
				},
			},
		);
	return (
		<section
			className={`ram ram--${kind}`}
			style={{"--ram-columns": columns.length}}
			aria-label="Statistiche della rosa"
		>
			<div className="ram__toolbar">
				{kind === "training" && (
					<ChoiceChips
						options={[
							{value: "attendance", label: "Presenze"},
							{value: "types", label: "Tipologie"},
						]}
						value={trainingView}
						onChange={setTrainingView}
					/>
				)}
				<ChoiceChips
					options={[
						{value: "week", label: "Settimana"},
						{value: "month", label: "Mese"},
					]}
					value={period}
					onChange={setPeriod}
				/>
				<div className="ram__navigation">
					<MainActions
						actions={[
							{
								icon: <ChevronLeftIcon />,
								title: "Periodo precedente",
								variant: "outlined",
								onClick: () =>
									setDate(
										moment(date).subtract(1, period).format("YYYY-MM-DD"),
									),
							},
						]}
					/>
					<div className="ram__date-picker">
						<VLFDatePicker
							placeholder="Data di riferimento"
							value={moment(date).toDate()}
							onChange={(d) => d && setDate(moment(d).format("YYYY-MM-DD"))}
						/>
					</div>
					<MainActions
						actions={[
							{
								icon: <ChevronRightIcon />,
								title: "Periodo successivo",
								variant: "outlined",
								onClick: () =>
									setDate(moment(date).add(1, period).format("YYYY-MM-DD")),
							},
						]}
					/>
				</div>
				<div className="ram__search">
					<VLFTextField
						name="search"
						margin="none"
						value={search}
						placeholder="Nome o cognome"
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
				{kind === "match" && (
					<div className="ram__competition">
						<VLFSelectNew
							aria-label="Competizione"
							noMargin
							sorted={false}
							isClearable={false}
							value={competition}
							options={options}
							onChange={setCompetition}
						/>
					</div>
				)}
				<Button
					className="ram__export"
					variant="outlined"
					startIcon={<PictureAsPdfIcon />}
					disabled={
						loading ||
						exporting ||
						!data ||
						(kind === "match" && (!columns.length || !rows.length))
					}
					onClick={async () => {
						setExporting(true);
						try {
							await generateRosterMatrixPDF({
								data,
								rows,
								kind,
								fromDate,
								toDate,
								search,
								competitionLabel: competition.label,
								trainingScope,
								trainingMetric,
								trainingView,
							});
						} catch {
							setError("Impossibile esportare il PDF. Riprova.");
						} finally {
							setExporting(false);
						}
					}}
				>
					{exporting ? "Esportazione..." : "Esporta PDF"}
				</Button>
			</div>
			<div className="ram__heading">
				<strong>{data?.seasonName || season?.name}</strong>
				<span>
					{moment(fromDate).format("DD/MM/YYYY")} –{" "}
					{moment(toDate).format("DD/MM/YYYY")}
				</span>
				<span>
					{columns.length} {kind === "training" ? "sedute" : "partite"} ·{" "}
					{rows.length} giocatori
				</span>
			</div>
			<p className="ram__note">
				{kind === "training"
					? "Le sedute programmate non entrano nei totali delle presenze. "
					: "Le partite programmate non entrano nei totali statistici. "}
				Rosa attuale e partecipanti registrati nel periodo; lo storico delle
				assegnazioni non viene ricostruito. Il simbolo ? indica un dato
				mancante, non un'assenza.
			</p>
			{kind === "training" &&
				trainingView === "types" &&
				!loading &&
				!error &&
				data && (
					<TrainingTypes
						typeColors={typeColors}
						analysis={data.trainingAnalysis}
						scope={trainingScope}
						setScope={setTrainingScope}
						metric={trainingMetric}
						setMetric={setTrainingMetric}
					/>
				)}
			{loading ? (
				<p role="status">Caricamento della matrice…</p>
			) : error ? (
				<p role="alert" className="ram__error">
					{error}
				</p>
			) : kind === "training" && trainingView === "types" ? (
				<TrainingTypesMatrix
					scope={trainingScope}
					typeColors={typeColors}
					metric={trainingMetric}
					rows={rows}
					summary={data?.trainingAnalysis?.period[trainingScope]}
				/>
			) : !columns.length ? (
				<p className="ram__empty">
					Nessun {kind === "training" ? "allenamento" : "incontro"} nel periodo
					selezionato.
				</p>
			) : (
				<>
					<div className="ram__legend">
						{statuses.map((key) => (
							<span key={key}>
								<span
									className={`ram__badge ram__badge--${MATRIX_STATUSES[key].tone}`}
								>
									{MATRIX_STATUSES[key].short}
								</span>
								{MATRIX_STATUSES[key].label}
							</span>
						))}
					</div>
					<div
						className="ram__scroll"
						tabIndex={0}
						role="region"
						aria-label={
							kind === "training"
								? "Matrice presenze allenamenti"
								: "Matrice impiego partite"
						}
					>
						<table className="ram__table">
							<thead>
								<tr>
									<th className="ram__player">Giocatore</th>
									{columns.map((c) => (
										<th
											key={c.key}
											className={
												c.scheduled ? "ram__scheduled-column" : undefined
											}
										>
											<button
												className="ram__column"
												onClick={() => open(c)}
												title={[
													moment(c.date).format("DD/MM/YYYY HH:mm"),
													c.label,
													c.detail,
													c.result,
												]
													.filter(Boolean)
													.join(" · ")}
											>
												<strong className="ram__date">
													{kind === "match" && (
														<span
															className={`ram__side ram__side--${c.matchSide}`}
															title={
																c.matchSide === "home"
																	? "In casa"
																	: "In trasferta"
															}
															aria-label={
																c.matchSide === "home"
																	? "In casa"
																	: "In trasferta"
															}
														>
															{c.matchSide === "home" ? (
																<HomeIcon />
															) : (
																<FlightIcon />
															)}
														</span>
													)}
													{moment(c.date).format("DD/MM")}
												</strong>
												{kind === "training" && (
													<span>{moment(c.date).format("HH:mm")}</span>
												)}
												{kind === "match" && (
													<>
														<small className="ram__opponent">
															{c.opponent}
														</small>
														<small>
															{c.detail || "Competizione non indicata"}
														</small>
														<b>
															{c.scheduled
																? c.timeToBeConfirmed
																	? "OdD"
																	: moment(c.date).format("HH:mm")
																: c.result || "—"}
														</b>
													</>
												)}
											</button>
										</th>
									))}
									<th>{kind === "training" ? "Presenze" : "Gare giocate"}</th>
									{kind === "match" && (
										<>
											<th>Minuti</th>
											{MATCH_MATRIX_SUMMARY_METRICS.map(([key, label]) => (
												<th className="ram__metric" key={key}>
													{label}
												</th>
											))}
										</>
									)}
									<th>Da completare</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((player) => {
									const totals = summarizeMatrixRow(player, kind);
									return (
										<tr key={player.playerId}>
											<th scope="row" className="ram__player">
												<Link to={"/players/" + player.playerId}>
													{player.number != null && player.number !== 0 && (
														<span className="ram__number">
															#{player.number}
														</span>
													)}
													{player.name}
												</Link>
												{player.guest && (
													<small>
														Partecipante da altra rosa / non più in rosa
													</small>
												)}
											</th>
											{columns.map((c) => {
												const cell = player.cells[c.key] || {
														status: "unrecorded",
													},
													status =
														MATRIX_STATUSES[cell.status] ||
														MATRIX_STATUSES.unrecorded;
												const title = [
													player.name,
													moment(c.date).format("DD/MM/YYYY"),
													c.label,
													status.label,
													cell.number != null ? "Maglia #" + cell.number : null,
													cell.minutes != null ? cell.minutes + " minuti" : "",
													...(kind === "match" && !c.scheduled
														? [
																"Gol: " + (cell.goals ?? "da rilevare"),
																"Assist: " + (cell.assists ?? "da rilevare"),
																"Gialli: " +
																	(cell.yellowCards ?? "da rilevare"),
																"Espulsioni: " +
																	(cell.redCards ?? "da rilevare"),
															]
														: []),
													cell.warning,
												]
													.filter(Boolean)
													.join(" · ");
												return (
													<td key={c.key}>
														<button
															className={`ram__cell ram__cell--${status.tone}`}
															onClick={() => open(c)}
															title={title}
															aria-label={title}
														>
															<span className="ram__badge">{status.short}</span>
															{kind === "match" && !c.scheduled && (
																<strong>
																	{cell.minutes != null
																		? cell.minutes + "′"
																		: "—"}
																</strong>
															)}
															<span className="ram__events">
																{cell.goals > 0 && (
																	<span title={cell.goals + " gol"}>
																		<SportsSoccerIcon />
																		{cell.goals}
																	</span>
																)}
																{cell.assists > 0 && (
																	<span title={cell.assists + " assist"}>
																		A {cell.assists}
																	</span>
																)}
																{cell.yellowCards > 0 && (
																	<span
																		className="ram__yellow"
																		title={cell.yellowCards + " gialli"}
																	>
																		■ {cell.yellowCards}
																	</span>
																)}
																{cell.redCards > 0 && (
																	<span className="ram__red" title="Espulsione">
																		■
																	</span>
																)}
																{cell.warning && (
																	<span title={cell.warning}>!</span>
																)}
															</span>
														</button>
													</td>
												);
											})}
											<td className="ram__total">
												<strong>
													{totals.attended}/{totals.total}
												</strong>
												{kind === "training" && (
													<small>
														{totals.total
															? Math.round(
																	(totals.attended / totals.total) * 100,
																)
															: 0}
														%
													</small>
												)}
											</td>
											{kind === "match" && (
												<td className="ram__total">
													<strong>{totals.minutes ?? "—"}</strong>
													<small>
														{totals.minutesKnown}{" "}
														{totals.minutesKnown === 1
															? "gara con dato"
															: "gare con dato"}
													</small>
												</td>
											)}
											{kind === "match" &&
												MATCH_MATRIX_SUMMARY_METRICS.map(([key, label]) => {
													const metric = matrixSummaryMetric(
														player,
														totals,
														key,
													);
													return (
														<td
															className="ram__metric"
															key={key}
															title={`${metric.conceded ? "Gol subiti" : label}: ${metric.known} gare con dato disponibile`}
														>
															<strong>{metric.value ?? "—"}</strong>
															{metric.conceded && (
																<small className="ram__conceded-label">
																	subiti
																</small>
															)}
														</td>
													);
												})}
											<td className="ram__total">{totals.unknown}</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
					{!rows.length && (
						<p className="ram__empty">Nessun giocatore trovato.</p>
					)}
					<p className="ram__note">
						Clicca una cella per aprire{" "}
						{kind === "training" ? "la seduta" : "la partita"}. Totali sui soli
						dati disponibili; i mancanti restano da completare.
						{kind === "match" &&
							" I minuti usano la fonte comune della gara: recupero escluso, supplementari inclusi. Il pallone indica i gol; A indica gli assist. La colonna Gol mostra i gol fatti per i giocatori di movimento e quelli subiti per i portieri (indicati con subiti)."}
					</p>
				</>
			)}
		</section>
	);
}
